import type React from "react";
import { useEffect, useState } from "react";

import { Stack } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useGetManagerMedicalReferral } from "api/hooks/useGetManagerMedicalReferral";
import { useGetManagerPlanOfCare } from "api/hooks/useGetManagerPlanOfCare";
import { getPatientCareEventsQueryKey } from "api/hooks/useGetPatientCareEventsQuery";
import { useGetPatientIcdCodesQuery } from "api/hooks/useGetPatientIcdCodes";
import { getPTCareEventsQueryKey } from "api/hooks/useGetPTCareEventsQuery";
import { useMergeCareEvent } from "api/hooks/usePutPatientCareEventMutation";
import type { CareEvent } from "api/models/CareEvent";
import type { Status } from "api/schemas/CareEventBilling";
import { useProfileContext } from "contexts/ProfileContext";
import { LazyLoadedPDF } from "routes/dashboard/physician/components/PDF";
import { MedicalNoteFormButtonsAdminBilling } from "routes/patients/PatientProfile/components/DynamicMedicalNotes/components/MedicalNoteFormButtonsAdminBilling";
import { MedicalNoteFormButtonsPhysician } from "routes/patients/PatientProfile/components/DynamicMedicalNotes/components/MedicalNoteFormButtonsPhysician";
import { signNote } from "routes/patients/queries/notesApiHandler";
import { Spinner } from "shared/atoms/Spinner";
import type { Market, MedicalNoteSectionConfig, MedicalNoteType } from "types";
import { MedicalNoteSectionTypes } from "types";
import { AnalyticsEvents, AnalyticsService } from "utils/analytics";
import { getPatientFullDetailsQueryKey } from "utils/contexts/providers/useGetPatientsFullDetailsQuery";
import { reportError } from "utils/errorReporting";
import { getMedicalNoteConfig, isChatNote, isKickOffNote } from "utils/medicalNotes";
import { isRTMNote } from "utils/medicalNotes/isNoteType";
import { delay } from "utils/misc/delay";

import {
  DynamicSection,
  ExclusionSection,
  MedicalNoteFormButtons,
  Section,
  SystemSuggestionsSection,
  TopSection,
} from "../../../../DynamicMedicalNotes/components";
import { reduceCareEventToFields } from "../../../../DynamicMedicalNotes/helpers/careEventHelpers";
import {
  getAllFieldNamesFromConfig,
  shouldShowSection,
} from "../../../../DynamicMedicalNotes/helpers/dynamicMedicalNoteHelpers";
import { ContentWrapper, Form, StyledCol } from "../../../../DynamicMedicalNotes/utils/formStyles";
import { ICDCodeBaseLookup, ICDCodes } from "../../utils/ICDCodes";
import type { ShowButtons } from "../../utils/types";
import type { AutosaveStatus } from "../AutosaveIndicator";

export type NoteFormCareEventProps = Pick<
  CareEvent,
  | "ailment"
  | "icd_codes"
  | "id"
  | "label"
  | "medical_record_note"
  | "medical_referral_id"
  | "pain_location"
  | "patient_billing_address"
  | "patient_email"
  | "patient_id"
  | "patient_phone_number"
  | "payer"
  | "plan_of_care_id"
  | "signed_limit_reached"
  | "status"
>;

interface Props {
  careEvent: NoteFormCareEventProps;
  open?: boolean;
  setAutosaveStatus?: (status: AutosaveStatus) => void;
  suggested?: boolean;
  isAdminBillingView?: boolean;
  isPhysician?: boolean;
  skipMerge?: boolean;
  readOnly?: boolean;
  showNoteHeader?: boolean;
  billingStatus?: Status;
  clearSelectedCareEvent?: () => void;
  discardFromDashboard?: () => void;
  startTime?: string;
}

export const DynamicMedicalNoteForm: React.FC<Props> = ({
  open = false,
  setAutosaveStatus,
  careEvent,
  suggested,
  isAdminBillingView,
  isPhysician,
  skipMerge = false,
  readOnly = false,
  showNoteHeader,
  billingStatus,
  clearSelectedCareEvent,
  discardFromDashboard,
  startTime,
}) => {
  const {
    ailment,
    id,
    label,
    medical_record_note,
    medical_referral_id,
    pain_location,
    patient_billing_address,
    patient_id,
    payer,
    plan_of_care_id,
    signed_limit_reached,
  } = careEvent;
  const { profile } = useProfileContext();
  invariant(profile, "Profile context not set");
  const [readonly, setReadonly] = useState(readOnly);
  const [confirmDisabled, setConfirmDisabled] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState<ShowButtons>({ isOpen: false, type: "info" });
  const queryClient = useQueryClient();
  const { mergeCareEvent, status } = useMergeCareEvent({ label, patient_id, skipMerge });
  const config = getMedicalNoteConfig(profile.market as Market, label as MedicalNoteType);
  const showCareEventPdfDownload = isAdminBillingView && isRTMNote(label);

  const { data: medicalReferral } = useGetManagerMedicalReferral(medical_referral_id as number, {
    enabled: Boolean(isAdminBillingView) && Boolean(medical_referral_id),
  });
  const { data: planOfCare } = useGetManagerPlanOfCare(plan_of_care_id as number, {
    enabled: Boolean(isAdminBillingView) && Boolean(plan_of_care_id),
  });

  const { data: availableICDCodes = [] } = useGetPatientIcdCodesQuery(
    {
      patientId: patient_id,
    },
    {
      enabled: isAdminBillingView,
    }
  );

  const fieldsNames = getAllFieldNamesFromConfig(config, false);

  const form = useForm({
    defaultValues: reduceCareEventToFields(fieldsNames, careEvent),
    mode: "onChange",
  });

  const { handleSubmit, watch, getValues } = form;
  const icdCodes = watch("icd_codes");

  useEffect(() => {
    if (setAutosaveStatus) {
      setAutosaveStatus(status as AutosaveStatus);
    }
  }, [status]);

  const onValueChange = async () => {
    await mergeCareEvent({
      careEventId: id,
      data: getValues(),
      skipInvalidation: true,
    });
  };

  const onSignNote = handleSubmit(async formData => {
    setReadonly(true);
    setConfirmDisabled(true);
    const data = { ...formData, medical_record_note: "" };

    try {
      await mergeCareEvent({
        careEventId: id,
        data,
        skipInvalidation: true,
      });

      const addition = data.icd_codes?.some(code => code?.includes(ICDCodeBaseLookup.exclusion))
        ? data.structured_fields?.exclusion_reason
        : null;
      await signNote(id, addition);

      // Refetch patient profile to update treatment_started and visibility
      if (isKickOffNote(label) || isChatNote(label)) {
        queryClient.invalidateQueries(
          getPatientFullDetailsQueryKey({
            patientId: patient_id,
            therapistId: profile.id,
            therapistMarket: profile.market ?? "",
          })
        );
      }

      AnalyticsService.track(AnalyticsEvents.PATIENTS.SIGN_NOTE, {
        patient: patient_id,
        medicalNoteType: label,
        market: profile?.market,
        ICDcode: data.icd_code,
      });
      setShowConfirmation({ isOpen: true, type: "success" });
      await delay(3000);
    } catch (error) {
      setReadonly(false);
      setShowConfirmation({ isOpen: true, type: "danger" });
      if (error instanceof Error || typeof error === "string")
        reportError("DynamicMedicalNoteForm Error", error, {
          action: "saveAndSignNote",
          context: {
            noteType: label,
            fields: Object.keys(formData).concat(
              Object.keys(formData.structured_fields ?? {}).map(f => `structured_fields.${f}`)
            ),
          },
        });
    } finally {
      queryClient.invalidateQueries(getPatientCareEventsQueryKey({ patientId: patient_id }));
      queryClient.invalidateQueries(getPTCareEventsQueryKey({ userId: profile.id }));
      setConfirmDisabled(false);
    }
  });

  const getSection = (section: MedicalNoteSectionConfig) => {
    const isExclusionCode = icdCodes?.some(code =>
      profile.market === "SE" ? code?.includes(ICDCodes.R52_9) : code?.includes(ICDCodes.R52)
    );
    const isForcedOptional = section.forcedOptional === "isExclusionCode" && isExclusionCode;

    // Check section condition
    if (!shouldShowSection(section, getValues)) {
      return null;
    }

    if (section.type === MedicalNoteSectionTypes.EXCLUSION) {
      return (
        <ExclusionSection
          readonly={readonly}
          visible={isExclusionCode}
          key="exclusion-section"
          onChange={onValueChange}
        />
      );
    }

    if (section.type === MedicalNoteSectionTypes.TOP)
      return (
        <TopSection
          readonly={readonly}
          fields={section.fields}
          key="top-section"
          legacyText={medical_record_note}
          onChange={onValueChange}
          label={label}
          patientId={careEvent.patient_id}
          payer={payer}
          showNoteHeader={showNoteHeader}
          ailment={ailment}
          painLocation={pain_location}
          patientBillingAddress={patient_billing_address}
        />
      );

    if (section.type === MedicalNoteSectionTypes.SUGGESTIONS && section.fields?.length)
      return <SystemSuggestionsSection fields={section.fields} key="system-suggestions-section" />;

    return (
      <DynamicSection
        key={`dynamic-section-${section.name}`}
        name={section.name}
        readonly={readonly}
        fields={section.fields}
        forcedOptional={isForcedOptional}
        onChange={onValueChange}
        label={label}
        payer={payer}
        signedLimitReached={signed_limit_reached}
        patientId={patient_id}
        ailment={ailment}
        painLocation={pain_location}
      />
    );
  };

  const getFooter = () => {
    if (isPhysician) {
      if (careEvent.status !== "pending") {
        return null;
      }

      return (
        <MedicalNoteFormButtonsPhysician
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          note={{ ...getValues(), id, label }}
          clearSelectedCareEvent={clearSelectedCareEvent}
          patientId={patient_id}
        />
      );
    }

    if (isAdminBillingView) {
      return (
        <>
          {(showCareEventPdfDownload || medicalReferral || planOfCare) && (
            <Section label="ASSETS" dataTestId="assets-section" collapsible readOnly={readonly}>
              <DownloadButtons>
                {showCareEventPdfDownload && (
                  <LazyLoadedPDF
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    label={label.toUpperCase()}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    medicalNote={careEvent}
                    availableICDCodes={availableICDCodes}
                  />
                )}
                {medicalReferral && (
                  <LazyLoadedPDF
                    label="PHYSICIAN_MEDICAL_REFERRAL"
                    medicalNote={medicalReferral}
                    availableICDCodes={availableICDCodes}
                  />
                )}
                {planOfCare && (
                  <LazyLoadedPDF
                    label="PHYSICIAN_PLAN_OF_CARE"
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    medicalNote={{ ...careEvent, ...planOfCare }}
                    availableICDCodes={availableICDCodes}
                  />
                )}
              </DownloadButtons>
            </Section>
          )}

          {billingStatus === "pending" && (
            <MedicalNoteFormButtonsAdminBilling cid={id} clearSelectedCareEvent={clearSelectedCareEvent} />
          )}
        </>
      );
    }

    return (
      <Stack direction={{ xs: "column", md: "row" }} alignItems="center" justifyContent="flex-end" m={2}>
        <MedicalNoteFormButtons
          cid={id}
          confirmDisabled={confirmDisabled}
          medicalNoteType={label}
          showConfirmation={showConfirmation}
          setShowConfirmation={setShowConfirmation}
          setReadOnly={setReadonly}
          discardFromDashboard={discardFromDashboard}
          startTime={startTime}
        />
      </Stack>
    );
  };

  if (suggested) {
    return <Spinner />;
  }

  return (
    <FormProvider {...form}>
      <Form $open={open} onSubmit={onSignNote}>
        <StyledCol data-testid="medical_note_form">
          <ContentWrapper>
            {config.map(section => getSection(section))}
            {getFooter()}
          </ContentWrapper>
        </StyledCol>
      </Form>
    </FormProvider>
  );
};

const DownloadButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;

  & > a {
    margin-left: 0;
  }
`;
